import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiDownloadPost } from '../../lib/api';
import { toast } from 'sonner';
import { FileCode, Download, Settings2, ShoppingCart, Wallet, Users, FileText, AlertTriangle, ArrowRight, X } from 'lucide-react';
import { formatISTDateTime } from '../../lib/format';

const BANNER_KEY = 'tally_mapping_ack_v1';

function TallyMappingBanner({ nav }) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(BANNER_KEY) === '1');
  if (dismissed) return null;
  const ack = () => { localStorage.setItem(BANNER_KEY, '1'); setDismissed(true); };
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-4 flex items-start gap-3" data-testid="tally-mapping-banner">
      <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-display font-bold text-sm text-amber-900">Before your first export — configure your Tally ledger names</div>
        <p className="text-xs text-amber-800 mt-1">
          Set up <strong>Ledger Mapping</strong> so every account head matches your Tally company file <strong>exactly</strong>.
          Mismatched names will create brand-new (stray) ledgers in Tally on import and split your books.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => nav('/app/finance/tally-mapping')} data-testid="banner-open-mapping" className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-[#2d3856] flex items-center gap-1.5">Open Ledger Mapping <ArrowRight size={12} /></button>
          <button onClick={ack} data-testid="banner-dismiss" className="px-3 py-1.5 border border-amber-300 text-amber-800 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-amber-100 flex items-center gap-1.5"><X size={12} /> I've set up my mappings</button>
        </div>
      </div>
    </div>
  );
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const curMonth = () => new Date().toISOString().slice(0, 7);

const CARDS = [
  { key: 'purchase', title: 'Purchase Vouchers', desc: 'POs / GRNs → Purchase', icon: ShoppingCart, endpoint: '/tally/export/purchase-vouchers', mode: 'range' },
  { key: 'payment', title: 'Payment Vouchers', desc: 'Petty cash · Fuel · Travel', icon: Wallet, endpoint: '/tally/export/payment-vouchers', mode: 'range' },
  { key: 'payroll', title: 'Payroll Vouchers', desc: 'Payroll run → Journal', icon: Users, endpoint: '/tally/export/payroll-vouchers', mode: 'month' },
  { key: 'ra_bill', title: 'RA Bill Vouchers', desc: 'RA bills → Sales', icon: FileText, endpoint: '/tally/export/ra-bill-vouchers', mode: 'range' },
];

export default function TallyExport() {
  const nav = useNavigate();
  const [pending, setPending] = useState({});
  const [log, setLog] = useState([]);
  const [range, setRange] = useState({ from: monthStartISO(), to: todayISO() });
  const [month, setMonth] = useState(curMonth());
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    apiGet('/tally/pending-counts').then(setPending).catch(() => {});
    apiGet('/tally/sync-log').then((r) => setLog(r.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const doExport = async (card) => {
    setBusy(card.key);
    try {
      let url = card.endpoint;
      let fname;
      if (card.mode === 'month') { url += `?month=${month}`; fname = `tally_${card.key}_${month}.xml`; }
      else { url += `?from=${range.from}&to=${range.to}`; fname = `tally_${card.key}_${range.from}_${range.to}.xml`; }
      await apiDownloadPost(url, fname);
      toast.success(`${card.title} exported`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed (need finance export permission)');
    } finally { setBusy(''); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="tally-export-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Tally Export</h1>
          <p className="text-sm text-slate-600 mt-1">Export vouchers as Tally Prime XML — your accounts team imports without re-keying.</p>
        </div>
        <button onClick={() => nav('/app/finance/tally-mapping')} data-testid="ledger-mapping-btn" className="px-4 py-2 border border-slate-200 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50 flex items-center gap-2"><Settings2 size={14} /> Ledger Mapping</button>
      </div>

      <TallyMappingBanner nav={nav} />

      <div className="flex flex-wrap gap-3 items-center bg-white border border-slate-200 rounded-sm p-3">
        <span className="text-[10px] font-bold uppercase text-slate-500">Date range (Purchase/Payment/RA):</span>
        <input type="date" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="tally-from" />
        <span className="text-slate-400 text-xs">to</span>
        <input type="date" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="tally-to" />
        <span className="text-[10px] font-bold uppercase text-slate-500 ml-4">Payroll month:</span>
        <input type="month" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="tally-month" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col" data-testid={`tally-card-${c.key}`}>
            <div className="flex items-start justify-between">
              <c.icon size={22} className="text-[#3b4872]" />
              {pending[c.key] > 0 && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700" data-testid={`pending-${c.key}`}>{pending[c.key]} pending</span>}
            </div>
            <div className="font-display font-bold text-sm mt-3">{c.title}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex-1">{c.desc}</div>
            <button onClick={() => doExport(c)} disabled={busy === c.key} data-testid={`export-${c.key}`} className="mt-3 w-full py-2 bg-[#3b4872] text-white rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50 flex items-center justify-center gap-1.5"><Download size={13} /> {busy === c.key ? 'Exporting…' : 'Export XML'}</button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><FileCode size={14} /> Sync Log</h3></div>
        {log.length === 0 ? <div className="p-4 text-xs text-slate-400">No exports yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500"><tr>{['Type', 'Range', 'Records', 'Exported By', 'When', 'Status'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {log.map((l) => (
                  <tr key={l.id} data-testid={`log-${l.id}`}>
                    <td className="px-3 py-2 font-display font-bold capitalize">{l.export_type}</td>
                    <td className="px-3 py-2 text-slate-500">{l.from_date || 'all'} → {l.to_date || 'all'}</td>
                    <td className="px-3 py-2">{l.record_count}</td>
                    <td className="px-3 py-2 text-slate-600">{l.exported_by || '—'}</td>
                    <td className="px-3 py-2 text-slate-500">{formatISTDateTime(l.exported_at)}</td>
                    <td className="px-3 py-2"><span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">{l.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
